import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../lib/i18n';
import { ArrowLeft, LifeBuoy, Mail, Phone, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';

const SUPPORT_EMAIL = 'support@soumissionpro.net';
const SUPPORT_PHONE = '438-792-2033';
const SUPPORT_PHONE_TEL = '+14387922033';

export default function Support() {
  const { lang } = useT();
  const isFr = lang === 'fr';

  return (
    <div className="min-h-screen bg-white" data-testid="support-page">
      <header className="sticky top-0 bg-white/95 backdrop-blur-xl border-b border-[#E5DCC9] z-10 pt-safe">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm font-bold text-gray-600"
            data-testid="support-back-link"
          >
            <ArrowLeft size={16} /> {isFr ? 'Retour' : 'Back'}
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 mb-4">
          <LifeBuoy size={20} className="text-[#7A1F2B]" />
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">
            {isFr ? 'Centre de support' : 'Support Center'}
          </div>
        </div>

        <h1 className="font-heading text-4xl font-black tracking-tighter mb-2">
          {isFr ? 'Nous sommes là pour vous aider' : 'We are here to help'}
        </h1>
        <p className="text-sm text-gray-600 mb-8 leading-relaxed">
          {isFr
            ? "Une question, un bogue, une suggestion ? Notre équipe répond habituellement en moins de 24 heures, du lundi au vendredi."
            : 'Question, bug, or suggestion? Our team usually replies within 24 hours, Monday to Friday.'}
        </p>

        {/* Contact cards */}
        <div className="grid sm:grid-cols-2 gap-3 mb-10">
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
              isFr ? 'Demande de support SoumissionPro' : 'SoumissionPro Support Request'
            )}`}
            className="group rounded-2xl border-2 border-[#E5DCC9] bg-white p-5 hover:border-[#7A1F2B] hover:shadow-lg transition-all"
            data-testid="support-email-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-xl bg-[#7A1F2B]/10 flex items-center justify-center">
                <Mail size={18} className="text-[#7A1F2B]" />
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                {isFr ? 'Courriel' : 'Email'}
              </div>
            </div>
            <div className="font-heading font-black text-base text-gray-900 break-all group-hover:text-[#7A1F2B]">
              {SUPPORT_EMAIL}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {isFr ? 'Réponse sous 24h' : 'Reply within 24h'}
            </div>
          </a>

          <a
            href={`tel:${SUPPORT_PHONE_TEL}`}
            className="group rounded-2xl border-2 border-[#E5DCC9] bg-white p-5 hover:border-[#7A1F2B] hover:shadow-lg transition-all"
            data-testid="support-phone-card"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-xl bg-[#7A1F2B]/10 flex items-center justify-center">
                <Phone size={18} className="text-[#7A1F2B]" />
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                {isFr ? 'Téléphone' : 'Phone'}
              </div>
            </div>
            <div className="font-heading font-black text-base text-gray-900 group-hover:text-[#7A1F2B]">
              {SUPPORT_PHONE}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {isFr ? 'Lun–Ven · 9h–17h (HE)' : 'Mon–Fri · 9am–5pm (ET)'}
            </div>
          </a>
        </div>

        {/* FAQ */}
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare size={16} className="text-[#7A1F2B]" />
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">
            {isFr ? 'Questions fréquentes' : 'Frequently Asked Questions'}
          </div>
        </div>

        <div className="space-y-2 mb-10">
          {(isFr ? FAQ_FR : FAQ_EN).map((item, i) => (
            <FaqItem key={i} q={item.q} a={item.a} index={i} />
          ))}
        </div>

        {/* CTA */}
        <div className="rounded-2xl bg-[#7A1F2B] text-white p-6 text-center">
          <h2 className="font-heading font-black text-xl mb-2">
            {isFr ? 'Vous ne trouvez pas votre réponse ?' : 'Can\u2019t find your answer?'}
          </h2>
          <p className="text-sm text-white/80 mb-4">
            {isFr
              ? 'Écrivez-nous directement, on répond en personne.'
              : 'Write to us directly — a human will reply.'}
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
              isFr ? 'Demande de support SoumissionPro' : 'SoumissionPro Support Request'
            )}`}
            className="inline-flex items-center gap-2 bg-white text-[#7A1F2B] font-bold text-sm px-5 py-3 rounded-xl hover:bg-[#E5DCC9] transition-colors"
            data-testid="support-cta-email"
          >
            <Mail size={16} /> {isFr ? 'Nous écrire' : 'Email us'}
          </a>
        </div>

        <div className="mt-10 pt-6 border-t border-[#E5DCC9] text-xs text-gray-500 text-center">
          <div className="mb-1">
            <b>SoumissionPro</b> · {isFr ? 'Montréal, Québec, Canada' : 'Montreal, Quebec, Canada'}
          </div>
          <div className="flex items-center justify-center gap-3 mt-2">
            <Link to="/privacy" className="hover:text-[#7A1F2B]" data-testid="support-privacy-link">
              {isFr ? 'Confidentialité' : 'Privacy'}
            </Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-[#7A1F2B]" data-testid="support-terms-link">
              {isFr ? "Conditions d'utilisation" : 'Terms'}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-[#E5DCC9] bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#FAF6EE] transition-colors"
        data-testid={`support-faq-toggle-${index}`}
      >
        <span className="font-heading font-bold text-sm text-gray-900">{q}</span>
        {open ? (
          <ChevronUp size={16} className="text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-gray-700 leading-relaxed" data-testid={`support-faq-answer-${index}`}>
          {a}
        </div>
      )}
    </div>
  );
}

const FAQ_FR = [
  {
    q: "Comment créer ma première soumission ?",
    a: "Depuis le tableau de bord, appuyez sur « Nouvelle soumission ». Suivez l'assistant en 4 étapes : client, matériaux/services, conditions, aperçu. Vous pouvez sauvegarder un brouillon à tout moment et y revenir plus tard, même hors ligne.",
  },
  {
    q: "L'application fonctionne-t-elle sans Internet ?",
    a: "Oui. SoumissionPro est conçue en mode hors ligne d'abord (offline-first). Vos soumissions sont sauvegardées localement sur votre appareil et synchronisées automatiquement dès que la connexion revient.",
  },
  {
    q: "Comment envoyer une soumission au client pour signature ?",
    a: "Ouvrez la soumission, appuyez sur « Envoyer pour signature ». Un lien sécurisé est généré et envoyé au client par courriel. Le client peut signer directement sur son téléphone — vous recevrez une notification dès qu'il signe ou refuse.",
  },
  {
    q: "Comment fonctionne mon essai gratuit et l'abonnement ?",
    a: "Vous bénéficiez d'un essai gratuit de 3 jours dès l'inscription. Toutes les fonctionnalités premium sont déverrouillées immédiatement. Une fois la période terminée, choisissez un forfait dans Profil → Abonnement. Vous pouvez annuler en tout temps.",
  },
  {
    q: "Comment récupérer mon mot de passe ?",
    a: "Sur l'écran de connexion, appuyez sur « Mot de passe oublié ». Saisissez votre courriel — vous recevrez un lien de réinitialisation valide 1 heure.",
  },
  {
    q: "Comment supprimer mon compte et mes données ?",
    a: "Allez dans Profil → Supprimer mon compte, ou visitez soumissionpro.net/supprimer-compte. La suppression est définitive après 30 jours (conformément à la Loi 25 du Québec).",
  },
  {
    q: "Le PDF généré ne s'affiche pas correctement, que faire ?",
    a: "Assurez-vous d'avoir la dernière version de l'application (fermez et rouvrez l'app). Si le problème persiste, envoyez-nous une capture d'écran à support@soumissionpro.net — on résout ça rapidement.",
  },
  {
    q: "Puis-je utiliser SoumissionPro avec mon équipe ?",
    a: "Oui. Le forfait Équipe permet d'inviter plusieurs utilisateurs sous un même compte d'entreprise, avec partage des clients, matériaux et soumissions. Voir Profil → Équipe.",
  },
];

const FAQ_EN = [
  {
    q: 'How do I create my first quote?',
    a: 'From the dashboard, tap "New quote". Follow the 4-step wizard: client, materials/services, conditions, preview. You can save a draft anytime and resume later — even offline.',
  },
  {
    q: 'Does the app work without Internet?',
    a: 'Yes. SoumissionPro is offline-first. Your quotes are saved locally on your device and sync automatically once you reconnect.',
  },
  {
    q: 'How do I send a quote to a client for signature?',
    a: 'Open the quote and tap "Send for signature". A secure link is emailed to the client. They can sign directly from their phone — you receive a notification the moment they sign or decline.',
  },
  {
    q: 'How does the free trial and subscription work?',
    a: 'You get a 3-day free trial on signup. All premium features are unlocked immediately. After the trial, pick a plan in Profile → Subscription. Cancel anytime.',
  },
  {
    q: 'How do I reset my password?',
    a: 'On the login screen, tap "Forgot password". Enter your email — you\u2019ll receive a reset link valid for 1 hour.',
  },
  {
    q: 'How do I delete my account and data?',
    a: 'Go to Profile → Delete my account, or visit soumissionpro.net/delete-account. Deletion is permanent after 30 days (compliant with Quebec Law 25).',
  },
  {
    q: 'The generated PDF doesn\u2019t look right, what should I do?',
    a: 'Make sure you have the latest version (close and reopen the app). If the issue remains, send us a screenshot at support@soumissionpro.net — we\u2019ll fix it fast.',
  },
  {
    q: 'Can I use SoumissionPro with my team?',
    a: 'Yes. The Team plan lets you invite multiple users under the same company account, with shared clients, materials and quotes. See Profile → Team.',
  },
];
